import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { 
    Box,
    ClickAwayListener,
    Typography,
    Avatar,
    Button,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import Loader from "../common/loader";
import StatusDot from "../common/StatusDot";
import EloBadge from "../common/EloBadge.jsx";
import utils from "../../universal/utils";
import { pickAvatarUpdatedAtMs, resolveAvatarSrc } from "../../utils/avatarUrl";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import { toProfilePath } from "../../universal/v2Routes";
import { DIALOG_ACCEPT_BUTTON_SX } from "../common/dialogActionButtonSx";
import FriendRowPreviewCard from "./FriendRowPreviewCard";
import { claimFriendRowMenu, subscribeFriendRowMenu } from "./friendRowMenu";

const INVITE_CLIP = "polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%)";

const basePath = utils.getEdgeBasePath();

function formatFriendPresenceLabel(isOnline, playerStatus) {
    if (!isOnline) return "OFFLINE";
    switch (playerStatus) {
        case "inMatch":
            return "IN MATCH";
        case "inMatchMaking":
            return "SEARCHING";
        case "inPrivateLobby":
        case "inYourLobby":
            return "IN TEAM";
        case "inLobby":
            return "IN LOBBY";
        case "offline":
            return "OFFLINE";
        default:
            return "ONLINE";
    }
}

export default function FriendRow({
    friend,
    handleDeleteFriend,
    sendInvite,
    inviteState = {},
    lobbyState,
    showUnavailableActionMessage,
    currentLobbyCode,
    isInMyLobby = false,
    isLobbyFull = false,
    isLeader = true,
    onProfileNavigate,
}) {
    const history = useHistory();
    const location = useLocation();
    const rowRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const friendData = friend.friendData;
    const friendKey = String(
        friendData?._id || friendData?.userId || friend.friendSteamId || "",
    );

    useEffect(() => {
        return subscribeFriendRowMenu((openId) => {
            if (openId !== friendKey) setMenuOpen(false);
        });
    }, [friendKey]);

    if (!friendData) {
        return null;
    }

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: friendData?.picture,
        userId: friendData?.userId || friendData?._id,
        avatarUpdatedAtMs: pickAvatarUpdatedAtMs(friendData),
        hasCustomAvatar: !!friendData?.avatar,
    });
    const isOnline = friendData ? friendData.isOnline : false;

    const playerStatus = friendData.playerStatus || (isOnline ? "inLobby" : null);

    const handleInvite = () => {
        sendInvite(friend);
    };

    const handleDelete = () => {
        handleDeleteFriend(friend.friendSteamId);
    };

    const handleProfile = () => {
        const profilePath = toProfilePath(friendData?._id, location.pathname);
        if (profilePath) {
            setMenuOpen(false);
            onProfileNavigate?.();
            history.push(profilePath);
        }
    };

    const handleToggleMenu = () => {
        setMenuOpen((prev) => {
            const next = !prev;
            if (next) claimFriendRowMenu(friendKey);
            return next;
        });
    };

    const presenceLabel = formatFriendPresenceLabel(isOnline, playerStatus);

    let presenceAccentSx = {};
    if (isOnline) {
        if (playerStatus === "inMatchMaking") {
            presenceAccentSx = { color: "rgba(255,190,90,0.72)" };
        } else if (playerStatus === "inMatch") {
            presenceAccentSx = { color: "rgba(120,200,255,0.78)" };
        }
    }

    const rowSx = {
        display: "flex",
        alignItems: "center",
        minHeight: "3rem",
        gap: { xs: "0.42rem", xl: "0.46rem" },
        padding: { xs: "0.28rem 0.5rem", xl: "0.3rem 0.6rem" },
        marginBottom: "0.08rem",
        borderRadius: "8px",
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)",
        border: `1px solid ${colors.border.default}`,
        transition:
            "background-color 180ms ease, border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
        position: "relative",
        overflow: "visible",
        cursor: "pointer",
        "&:hover": {
            background:
                "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.022) 100%)",
            border: `1px solid ${alpha(colors.primary.main, 0.5)}`,
            transform: menuOpen ? "none" : "translateY(-2px)",
        },
        ...(!isOnline && {
            opacity: 0.74,
            "&:hover": { opacity: 0.88, transform: "none" },
        }),
        ...(menuOpen && {
            "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "1.05rem",
                borderRadius: "999px",
                background: "#fff",
                pointerEvents: "none",
                zIndex: 2,
            },
        }),
    };

    const appearsDisconnected = !isOnline;

    const sxStatusChipLabel = {
        "& .MuiTypography-root": {
            fontSize: "inherit",
            fontWeight: "inherit",
            letterSpacing: "inherit",
            textTransform: "inherit",
            lineHeight: 2,
            color: "inherit",
        },
    };

    const sxInvitedWait = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.35rem",
        background: "rgba(255,255,255,0.05)",
        color: "rgba(240,242,246,0.92)",
        textTransform: "uppercase",
        fontSize: { xs: "0.58rem", xl: "0.6rem" },
        fontWeight: 600,
        letterSpacing: "0.05em",
        padding: { xs: "0.14rem 0.46rem", xl: "0.15rem 0.5rem" },
        borderRadius: "5px",
        border: "1px solid rgba(255,190,90,0.28)",
        userSelect: "none",
        transition: "all 180ms ease",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        ...sxStatusChipLabel,
    };

    const sxInTeam = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.28rem",
        background: "rgba(255,190,90,0.08)",
        color: "rgba(248,248,252,0.92)",
        textTransform: "uppercase",
        fontSize: { xs: "0.56rem", xl: "0.58rem" },
        fontWeight: 600,
        padding: { xs: "0.14rem 0.42rem", xl: "0.15rem 0.46rem" },
        borderRadius: "5px",
        border: "1px solid rgba(255,190,90,0.35)",
        userSelect: "none",
        transition: "all 180ms ease",
        letterSpacing: "0.05em",
        ...sxStatusChipLabel,
    };

    const sxTeamFull = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.28rem",
        background: "rgba(156, 163, 175, 0.06)",
        color: "#9ca3af",
        textTransform: "uppercase",
        fontSize: { xs: "0.56rem", xl: "0.58rem" },
        fontWeight: 600,
        letterSpacing: "0.05em",
        padding: { xs: "0.14rem 0.42rem", xl: "0.15rem 0.46rem" },
        borderRadius: "5px",
        border: "1px solid rgba(156, 163, 175, 0.22)",
        userSelect: "none",
        transition: "all 180ms ease",
        cursor: "default",
        opacity: 0.72,
        ...sxStatusChipLabel,
    };

    const inviteButtonSx = {
        ...DIALOG_ACCEPT_BUTTON_SX,
        minHeight: 26,
        minWidth: 0,
        px: "0.68rem",
        py: 0,
        borderRadius: "6px",
        clipPath: INVITE_CLIP,
        color: colors.text.contrast,
        fontSize: { xs: "0.64rem", xl: "0.66rem" },
        fontWeight: 750,
        letterSpacing: "0.05em",
        textTransform: "none",
        lineHeight: 1,
        whiteSpace: "nowrap",
        "& .MuiButton-startIcon": {
            marginLeft: 0,
            marginRight: "0.28rem",
            color: "inherit",
        },
        "& .MuiButton-startIcon > *:nth-of-type(1)": {
            fontSize: "0.88rem",
        },
        "&:active:not(:disabled)": {
            filter: "brightness(0.98)",
        },
    };

    const inviteButtonDisabledSx = {
        color: alpha("#fff", 0.38),
        border: "1px solid rgba(255,255,255,0.06)",
        background: `
            linear-gradient(
                180deg,
                rgba(255,255,255,0.045) 0%,
                rgba(255,255,255,0.02) 100%
            )
        `,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        filter: "none",
        cursor: "default",
        "&::after": {
            opacity: 0,
        },
        "&:hover": {
            filter: "none",
            borderColor: "rgba(255,255,255,0.06)",
            background: `
                linear-gradient(
                    180deg,
                    rgba(255,255,255,0.045) 0%,
                    rgba(255,255,255,0.02) 100%
                )
            `,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        },
    };

    return (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
        <Box>
        <Box
            ref={rowRef}
            role="button"
            tabIndex={0}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            onClick={handleToggleMenu}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleToggleMenu();
                }
            }}
            sx={rowSx}
        >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: "2.2rem", xl: "2.5rem" },
                        height: { xs: "2.2rem", xl: "2.5rem" },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `
                            radial-gradient(
                                circle at top,
                                rgba(246,185,76,0.16),
                                rgba(255,255,255,0.03)
                            )
                        `,
                        transition: "all 260ms ease",
                        border: appearsDisconnected
                            ? "1px solid rgba(255,255,255,0.04)"
                            : "1px solid rgba(255,255,255,0.08)",
                        boxShadow: appearsDisconnected
                            ? `
                                0 0 0 1px rgba(255,255,255,0.02),
                                0 0 20px rgba(0,0,0,0.4)
                            `
                            : `
                                inset 0 1px 0 rgba(255,255,255,0.06),
                                0 10px 24px rgba(0,0,0,0.35),
                                0 0 30px rgba(246,185,76,0.08)
                            `,
                    }}
                >
                    <Avatar
                        src={avatarSrc}
                        alt={friendData.nickname}
                        sx={{
                            position: "relative",
                            width: "90%",
                            height: "90%",
                            border: "2px solid rgba(255,255,255,0.08)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                            filter: appearsDisconnected
                                ? "grayscale(0.9) brightness(0.72)"
                                : "none",
                            opacity: appearsDisconnected ? 0.82 : 1,
                        }}
                    />
                </Box>
                <StatusDot
                    online={isOnline}
                    variant="avatar"
                    animateGlow={false}
                    sx={{
                        bottom: "-1px",
                        right: "-1px",
                        fontSize: { xs: "0.52rem", xl: "0.56rem" },
                    }}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "0.18rem",
                    minWidth: 0,
                    py: "0.05rem",
                }}
            >
                <Typography
                    sx={{
                        color: !isOnline ? colors.text.secondary : colors.text.primary,
                        fontSize: { xs: "0.85rem", xl: "0.9rem" },
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        lineHeight: 1.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {friendData.nickname}
                </Typography>

                <Typography
                    component="span"
                    sx={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "0.6rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: !isOnline
                            ? colors.text.muted
                            : colors.text.secondary,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        ...presenceAccentSx,
                    }}
                >
                    {presenceLabel}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    alignSelf: "center",
                    gap: "0.5rem",
                    flexShrink: 0,
                    pl: "0.25rem",
                }}
            >
                <EloBadge value={friendData.elo} size="small" />
                {isOnline && (
                    <>
                        {inviteState.inviteError ? null : inviteState.invited &&
                          !inviteState.inviteAccepted ? (
                            <Box sx={sxInvitedWait}>
                                <Loader />
                                <Typography>Invited...</Typography>
                            </Box>
                        ) : isInMyLobby ? (
                            <Box sx={sxInTeam}>
                                <Typography>In Your Lobby</Typography>
                            </Box>
                        ) : playerStatus === "inPrivateLobby" &&
                          !inviteState.invited &&
                          !inviteState.inviteAccepted ? (
                            <Box sx={sxInTeam}>
                                <Typography>In a Team</Typography>
                            </Box>
                        ) : playerStatus === "inLobby" &&
                          !inviteState.invited &&
                          !inviteState.inviteAccepted ? (
                            !isLeader && lobbyState !== "searching" && currentLobbyCode ? (
                                <Box sx={sxInTeam}>
                                    <Typography>Leader Only</Typography>
                                </Box>
                            ) : isLobbyFull ? (
                                <Box sx={sxTeamFull}>
                                    <Typography>Team Full</Typography>
                                </Box>
                            ) : (
                                <Button
                                    disableElevation
                                    disableRipple
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (lobbyState === "searching") {
                                            showUnavailableActionMessage();
                                        } else {
                                            handleInvite();
                                        }
                                    }}
                                    startIcon={<PersonAdd />}
                                    sx={{
                                        ...inviteButtonSx,
                                        ...(lobbyState === "searching" ? inviteButtonDisabledSx : {}),
                                    }}
                                >
                                    Invite
                                </Button>
                            )
                        ) : null}
                    </>
                )}

            </Box>
        </Box>
        <FriendRowPreviewCard
            open={menuOpen}
            anchorEl={rowRef.current}
            friendData={friendData}
            avatarSrc={avatarSrc}
            isOnline={isOnline}
            playerStatus={playerStatus}
            onProfile={handleProfile}
            onRemove={() => {
                setMenuOpen(false);
                handleDelete();
            }}
            onClose={() => setMenuOpen(false)}
        />
        </Box>
        </ClickAwayListener>
    );
}
